import React, { useState } from 'react';
import { useAuth } from '../../../firebase/AuthProvider';

// --- 1. TYPE DEFINITIONS & API SERVICE ---
// In a real application, these would be imported from your ReportApiService.ts file.
// For this example, they are included directly for clarity.

enum ReportPeriod {
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUARTERLY',
    ANNUAL = 'ANNUAL',
    CUSTOM = 'CUSTOM',
}

interface ReportGenerationRequest {
    period: ReportPeriod;
    startDate?: string;
    endDate?: string;
    teamId?: number;
    employeeId?: string;
}

interface ReportGenerationResponse {
    message: string;
    pdfReportId: string;
    jsonReportId: string;
}

interface ReportStatusResponse {
    id: string;
    status: 'PENDING' | 'COMPLETE' | 'FAILED';
    reportName: string;
    completedAt: string | null;
    blobStoragePath: string | null;
}

// Represents the data structure of the JSON report content
interface OverdueTaskData {
    participantName: string;
    taskTitle: string;
    assignedScName: string;
    dueDate: string;
    daysOverdue: number;
}

class ReportApiService {
    private readonly baseUrl: string;
    private readonly authToken: string;

    constructor(baseUrl: string, authToken: string) {
        this.baseUrl = baseUrl;
        this.authToken = authToken;
    }

    public async generateOverdueTasksReport(request: ReportGenerationRequest): Promise<ReportGenerationResponse> {
        const response = await fetch(`${this.baseUrl}/compliance/reports/overdue`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.authToken}` },
            body: JSON.stringify(request),
        });
        if (response.status !== 202) {
            const errorBody = await response.json();
            throw new Error(`API Error: ${JSON.stringify(errorBody)}`);
        }
        return response.json();
    }

    public async getReportStatus(reportId: string): Promise<ReportStatusResponse> {
        const response = await fetch(`${this.baseUrl}/compliance/reports/status/${reportId}`, {
            headers: { 'Authorization': `Bearer ${this.authToken}` }
        });
        if (!response.ok) throw new Error('Failed to get report status.');
        return response.json();
    }

    // New method to fetch the JSON content and provide a download URL for the PDF
    public getFileDownloadUrl(blobPath: string): string {
        // In a real app, this might be a dedicated authenticated endpoint.
        // For simplicity, we assume the blob storage is accessible or the URL has a SAS token.
        // This example just constructs a potential URL.
        const storageBaseUrl = "https://your_storage_account.blob.core.windows.net/your_container_name";
        return `${storageBaseUrl}/${blobPath}`;
    }

    public async getReportJsonData(blobPath: string): Promise<OverdueTaskData[]> {
        const url = this.getFileDownloadUrl(blobPath);
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch JSON report data.");
        return response.json();
    }
}


// --- 2. REACT COMPONENT ---

const ReportsPage: React.FC = () => {
    // --- State Management ---
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pdfReportStatus, setPdfReportStatus] = useState<ReportStatusResponse | null>(null);
    const [jsonReportStatus, setJsonReportStatus] = useState<ReportStatusResponse | null>(null);
    const [reportData, setReportData] = useState<OverdueTaskData[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [apiService, setApiService] = useState<ReportApiService>()

    const { currentUser } = useAuth();

    // --- API Configuration ---
    // IMPORTANT: Replace with your actual API URL and a method to get the user's token.


    // --- Event Handler ---
    const handleGenerateReport = async () => {
        setIsLoading(true);
        setError(null);
        setPdfReportStatus(null);
        setJsonReportStatus(null);
        setReportData(null);

        const uid = await currentUser?.getIdToken();
        console.log(uid)
        if (!uid) return;

        const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
        const sampleApiService = new ReportApiService(baseUrl, uid);


        const reportRequest: ReportGenerationRequest = { period: ReportPeriod.MONTHLY };
        setApiService(sampleApiService)

        try {
            const initialResponse = await sampleApiService.generateOverdueTasksReport(reportRequest);

            // Start polling for both files
            pollStatus(initialResponse.pdfReportId, setPdfReportStatus, 'PDF');
            pollStatus(initialResponse.jsonReportId, setJsonReportStatus, 'JSON');

        } catch (e) {
            setError((e as Error).message);
            setIsLoading(false);
        }
    };

    const pollStatus = async (
        reportId: string,
        setStatus: React.Dispatch<React.SetStateAction<ReportStatusResponse | null>>,
        type: 'PDF' | 'JSON'
    ) => {

        const uid = await currentUser?.getIdToken();
        if (!uid) return;

        const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
        const apiService = new ReportApiService(baseUrl, uid);
        const intervalId = setInterval(async () => {
            try {
                const status = await apiService.getReportStatus(reportId);
                setStatus(status);

                if (status.status === 'COMPLETE' || status.status === 'FAILED') {
                    clearInterval(intervalId);
                    if (status.status === 'COMPLETE' && type === 'JSON' && status.blobStoragePath) {
                        const data = await apiService.getReportJsonData(status.blobStoragePath);
                        setReportData(data);
                    }
                    if (pdfReportStatus?.status === 'COMPLETE' && jsonReportStatus?.status === 'COMPLETE') {
                        setIsLoading(false);
                    }
                }
            } catch (e) {
                setError((e as Error).message);
                clearInterval(intervalId);
                setIsLoading(false);
            }
        }, 5000); // Poll every 5 seconds
    };

    // --- Rendering Logic ---
    return (
        <div style={{ fontFamily: 'sans-serif', padding: '2rem' }}>
            <h1>Overdue Tasks Reports</h1>
            <p>Generate a report for the current month.</p>

            <button onClick={handleGenerateReport} disabled={isLoading} style={{ padding: '10px 15px', fontSize: '16px', cursor: 'pointer' }}>
                {isLoading ? 'Generating...' : 'Generate Monthly Report'}
            </button>

            {error && <p style={{ color: 'red' }}>Error: {error}</p>}

            <div style={{ marginTop: '2rem', display: 'flex', gap: '2rem' }}>
                {/* PDF Status and Download */}
                <div style={{ flex: 1 }}>
                    <h2>PDF Report</h2>
                    {pdfReportStatus ? (
                        <div>
                            <p>Status: <strong>{pdfReportStatus.status}</strong></p>
                            {pdfReportStatus.status === 'COMPLETE' && pdfReportStatus.blobStoragePath && (
                                <a href={apiService?.getFileDownloadUrl(pdfReportStatus.blobStoragePath)} target="_blank" rel="noopener noreferrer">
                                    <button style={{ padding: '8px 12px', background: '#28a745', color: 'white', border: 'none', cursor: 'pointer' }}>
                                        Download PDF
                                    </button>
                                </a>
                            )}
                        </div>
                    ) : <p>Awaiting generation...</p>}
                </div>

                {/* JSON Status and Display */}
                <div style={{ flex: 1 }}>
                    <h2>JSON Data Report</h2>
                    {jsonReportStatus ? (
                        <p>Status: <strong>{jsonReportStatus.status}</strong></p>
                    ) : <p>Awaiting generation...</p>}
                </div>
            </div>

            {/* Render the JSON data in a table */}
            {reportData && (
                <div style={{ marginTop: '2rem' }}>
                    <h2>Report Data Viewer</h2>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f2f2f2' }}>
                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Participant</th>
                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Task</th>
                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Assigned SC</th>
                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Due Date</th>
                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Days Overdue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.map((row, index) => (
                                <tr key={index}>
                                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{row.participantName}</td>
                                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{row.taskTitle}</td>
                                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{row.assignedScName}</td>
                                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{new Date(row.dueDate).toLocaleDateString()}</td>
                                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{row.daysOverdue}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ReportsPage;
